// This API queries the proxy middleware in ASP to directly get Drupal data
// The generated types allow parsing of the responses,
// which will then be transformed to match the requirements of the components

import { Iterable, List, Map, OrderedMap, Set } from 'immutable'
import * as Moment from 'moment'
import { ComplexFilterExpresion, FilterBuilderComplex, SelectedQuery, SelectedRelationQuery } from 'typescript-odata-client'
import { Fun, HttpResult } from 'widgets-for-react'
import { fixMediaUrl } from '../../Shared/api/drupal/drupalApi'
import { node_parser } from '../../Shared/drupal_models/parser'
import { Node } from '../../Shared/drupal_models/types'
import { lastClusterOfDigits } from '../../Shared/utils/string'
import { ActivityClusterProps } from '../custom_components/components/activity_cluster'
import { ActivitySummaryProps } from '../custom_components/components/activity_summary'
import { DateRangeEventCount, Page } from '../custom_components/components/page_renderer'
import { PeriodSlug } from '../custom_components/components/region_and_date_filter'
import { OverviewVacancy } from '../custom_components/components/vacancy_overview_block'
import * as QueryContext from '../custom_components/odata_context'
import { RegionInfo } from '../custom_components/pages/activity_clusters_page'
import { CollectionPageProps } from '../custom_components/pages/collection_page'
import { GooglePlacesOpeningHours } from '../custom_components/pages/location_detail_page'
import { ActivityEditionN, ActivityEvent } from '../custom_models/activity_edition'
import * as DrupalModel from '../custom_models/drupal'
import { Report } from '../custom_models/report'
import { moment } from '../custom_utils/moment'
import { Option } from '../custom_utils/option'
import { defaultGetOptions, handle } from './api_utils'
import * as ConvertNodeTo from './drupal_node_conversion'


const odataBaseUrl = `/api/v1/OData/GetQuery?Query=`

export type ApiResult<A> = "loading" | "error" | A;

interface DatePeriod {
  start: Moment.Moment;
  end: Moment.Moment;
}

export type Region = {
  id: number
  slug: string
  name: string
  locations: List<number>
}

export const loadingText = "Bezig met laden...";
export const errorText = "De data kon niet worden geladen.";
export const noResultsText = "Er zijn geen resultaten gevonden.";

const drupalUrl = "/api/drupal";

const catchError: <A>() => (e: any) => ApiResult<A> = <A>() => e => {
  console.log(e);
  return "error" as ApiResult<A>;
};

const get: <A>(url: string) => Promise<ApiResult<A>> = <A>(url) => {
  return fetch(url)
    .then(x => (x.status == 200 ? x.json() : Promise.reject(x.statusText)))
    .then((x: A) => x as ApiResult<A>)
    .catch(catchError<A>());
};

export const getCollectionPage = async (): Promise<HttpResult<CollectionPageProps>> => {
  const res = await fetch(drupalUrl + '/collectie', defaultGetOptions())

  const temp = await handle<Node>(res)
  if (temp.kind != 'result') return (temp as HttpResult<CollectionPageProps>)

  const n = node_parser(temp.value);
  if (n.bundle != "collection_overview") throw new Error("/collection is not a 'collection_overview'");
  const p = ConvertNodeTo.collectionPage(n)
  return { ...temp, value: p }
}

export const getDatesForPeriod: (p: PeriodSlug) => DatePeriod = p => {
  let start = Moment().startOf('day')
  let end = Moment().endOf('day')

  start.add(p == 'morgen' ? 1 : 0, 'days')
  end.add(
    p == 'vandaag' ? 0
      : p == 'morgen' ? 1
        : p == 'komende-zeven-dagen' ? 7
          : p == 'komende-dertig-dagen' ? 30
            : 9999,
    'days'
  )

  return { start, end }
};

export interface SpecialDay {
  Date: Date
  ExeptionalHours: boolean
}
export interface Periods {
  Close: {
    Day: number, 
    Hour: number, 
    Minute: number,
    Truncated: string,
    Date: string
  },
  Open: {
    Day: number,
    Hour: number, 
    Minute: number, 
    Truncated: string,
    Date: string
  }
}

export enum SecondaryHoursType {
  SECONDARY_HOURS_TYPE_UNSPECIFIED = 0,
  DRIVE_THROUGH = 1,
  HAPPY_HOUR = 2,
  DELIVERY = 3,
  TAKEOUT = 4,
  KITCHEN = 5,
  BREAKFAST = 6,
  LUNCH = 7,
  DINNER = 8,
  BRUNCH = 9,
  PICKUP = 10,
  ACCESS = 11,
  SENIOR_HOURS = 12,
  ONLINE_SERVICE_HOURS = 13,
}

export interface GooglePlacesResponse {
  PlaceId: string, 
  CurrentOpeningHours: {
    SpecialDays: SpecialDay[]
    Periods: Periods[],
    Type: SecondaryHoursType,
    WeekdayText: string[] 
  }
}

export const getGooglePlacesOpeningHours = async (id: string): Promise<ApiResult<GooglePlacesOpeningHours>> => {
  const res = await fetch(`/api/v1/places/${id}`)
  if (!res.ok) return "error"

  try {
    const data: GooglePlacesResponse = await res.json()
    const googlePlacesOpeningHours : GooglePlacesOpeningHours = {
      specialDays: data.CurrentOpeningHours.SpecialDays,
      periods: data.CurrentOpeningHours.Periods.map((p) => ({
        close: {
          day: p.Close.Day, 
          hour: p.Close.Hour, 
          minute: p.Close.Minute,
          readableTime: `${p.Close.Hour < 10 ? `0${p.Close.Hour}` : `${p.Close.Hour}`}:${p.Close.Minute < 10 ? `0${p.Close.Minute}` : `${p.Close.Minute}`}`
        },
        open: {
          day: p.Open.Day, 
          hour: p.Open.Hour, 
          minute: p.Open.Minute,
          readableTime: `${p.Open.Hour < 10 ? `0${p.Open.Hour}` : `${p.Open.Hour}`}:${p.Open.Minute < 10 ? `0${p.Open.Minute}` : `${p.Open.Minute}`}`
        }
      })),
      type: data.CurrentOpeningHours.Type,
      weekdayText: data.CurrentOpeningHours.WeekdayText
    }
    return googlePlacesOpeningHours as ApiResult<GooglePlacesOpeningHours>
  }
  catch (error) {
    return "error"
  }
}

const previewUrl = "/api";

const constructUrl = (route: string, params: string): string => {
  const preview = new RegExp("^/node-preview/").test(route);
  if (preview) {
    return `${previewUrl}${route}${params}`
  }
  return `${drupalUrl}${route}`
}

export const getPage: (route: string, params: string) => Promise<ApiResult<Page>> = (r, p) =>
  get<Node>(constructUrl(r, p)).then(x => {
    if (x == "loading" || x == "error") return "error";

    try {
      const n = node_parser(x);

      const p =
        n.bundle == "activity_clusters"
          ? ConvertNodeTo.activityClustersPage(n)
        : n.bundle == "activity_detail"
          ? ConvertNodeTo.activityDetailPage(n)
        : n.bundle == "collection_overview"
          ? ConvertNodeTo.collectionPage(n)
        : n.bundle == "location_detail"
          ? ConvertNodeTo.locationDetailPage(n)
        : n.bundle == "home"
          ? ConvertNodeTo.homePage(n)
        : n.bundle == "report_overview"
          ? ConvertNodeTo.reportOverview(n)
        : n.bundle == "faq"
          ? 'error' // This content type is not a page
        : n.bundle == 'blog_overview'
          ? 'error'
        : n.bundle =='blog_post'
          ? 'error'
        : n.bundle == 'location'
          ? 'error'
        : n.bundle == 'location_overview'
          ? 'error'
        : n.bundle == 'news_item'
          ? 'error'
        : n.bundle == 'news_overview'
          ? 'error'
        : n.bundle == 'newsletter'
          ? 'error'
        : ConvertNodeTo.generalContentPage(n);

      return p as ApiResult<Page>;
    } catch (e) {
      console.log(e);
      console.log("Are you sure all the required fields in Drupal are filled?");
    }

    return 'error';
  });

export const getRegions: () => Promise<ApiResult<Map<string, Region>>> = () => {
  let regions = QueryContext.RegionQuery(odataBaseUrl)
    .Select("Id", "Title", 'Slug')
    .IncludeStrict("Region_Location", q => q.Select("RegionId", "LocationId"))
    .ToList()

  return regions.then(r => {
    try {
      return r.reduce(
        (a, e) => a.set(
          e.Title,
          {
            id: e.Id,
            slug: e.Slug,
            name: e.Title,
            locations: e.Region_Location.map(l => l.LocationId).toList()
          }
        ),
        Map<string, Region>()
      )
        .sort((a, b) => a.slug.localeCompare(b.slug))
        .toOrderedMap() as ApiResult<Map<string, Region>>

    } catch (e) {
      console.error(e)
      return "error" as ApiResult<any>
    }
  })
};

type SelectCondition<S> = (s: S) => S
const selectWhere = <Q>(q: Q, c: SelectCondition<Q>) => c(q)

type ActivityClusterPick = Pick<QueryContext.ActivityCluster, "Id" | "Title" | "Description" | "Slug">
export type ActivityClusterSelectedQuery = SelectedQuery<QueryContext.ActivityCluster, ActivityClusterPick>
export type ActivityClusterSelectedRelationQuery = SelectedRelationQuery<QueryContext.ActivityCluster, ActivityClusterPick>

type ActivityPick = Pick<QueryContext.Activity, "Id" | "Title" | "Sticky" | "HideInOverview" | "StickyTip">
export type ActivitySelectedQuery = SelectedQuery<QueryContext.Activity, ActivityPick>
export type ActivitySelectedRelationQuery = SelectedRelationQuery<QueryContext.Activity, ActivityPick>

type EditionPick = Pick<QueryContext.ActivityEditionN, "Id" | "SignupText" | "SignupURL" | "Cost">
export type EditionSelectedQuery = SelectedQuery<QueryContext.ActivityEditionN, EditionPick>
export type EditionSelectedRelationQuery = SelectedRelationQuery<QueryContext.ActivityEditionN, EditionPick>

type EventPick = Pick<QueryContext.ActivityEvent, "Id" | "Date" | "Start" | "End">
export type EventSelectedQuery = SelectedQuery<QueryContext.ActivityEvent, EventPick>
export type EventSelectedRelationQuery = SelectedRelationQuery<QueryContext.ActivityEvent, EventPick>

type LocationPick = Pick<QueryContext.Location, "Id" | "Title">
export type LocationSelectedQuery = SelectedQuery<QueryContext.Location, LocationPick>
export type LocationSelectedRelationQuery = SelectedRelationQuery<QueryContext.Location, LocationPick>

type RegionPick = Pick<QueryContext.Region, "Id" | "Title" | "Slug">
export type RegionSelectedQuery = SelectedQuery<QueryContext.Region, RegionPick>
export type RegionSelectedRelationQuery = SelectedRelationQuery<QueryContext.Region, RegionPick>

export type QueryConditions<
  C extends ActivityClusterSelectedQuery | ActivityClusterSelectedRelationQuery,
  A extends ActivitySelectedQuery | ActivitySelectedRelationQuery,
  E extends EditionSelectedQuery | EditionSelectedRelationQuery,
  R extends RegionSelectedQuery | RegionSelectedRelationQuery,
  L extends LocationSelectedQuery | LocationSelectedRelationQuery,
  V extends EventSelectedQuery | EventSelectedRelationQuery
  > = {
    cluster: Option<SelectCondition<C>>,
    activity: Option<SelectCondition<A>>,
    edition: Option<SelectCondition<E>>,
    region: Option<SelectCondition<R>>,
    location: Option<SelectCondition<L>>,
    event: Option<SelectCondition<V>>
  }

export const getActivityClusters: (f: QueryConditions<ActivityClusterSelectedQuery, ActivitySelectedRelationQuery, EditionSelectedRelationQuery, RegionSelectedRelationQuery, LocationSelectedRelationQuery,
  EventSelectedRelationQuery>) => Promise<ApiResult<List<ActivityClusterProps>>> =
  f => {
    let q = selectWhere(
      QueryContext
        .ActivityClusterQuery(odataBaseUrl)
        .Select('Id', 'Title', 'Description', 'Slug'),
      c => f.cluster.kind == 'some' ? f.cluster.value(c) : c
    )
      .IncludeStrict('ActivityCluster_Activity', q => q
        .Select('ActivityId', 'ActivityClusterId')
        .IncludeStrict('Activity', q =>
          selectWhere<SelectedRelationQuery<QueryContext.Activity, ActivityPick>>(
            q.Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'),
            a => f.activity.kind == "some" ? f.activity.value(a) : a
          )
            .IncludeStrict('Activity_ActivityEditionN', q => q
              .Select('ActivityId', 'ActivityEditionNId')
              .IncludeStrict('ActivityEditionN', q =>
                selectWhere(
                  q.Select('Id', 'SignupText', 'SignupURL', 'Cost'),
                  e => f.edition.kind == 'some' ? f.edition.value(e) : e
                )
                  .IncludeStrict('ActivityEditionN_ActivityEvent', ae => ae
                    .Select('ActivityEditionNId', 'ActivityEventId')
                    .IncludeStrict('ActivityEvent', ev =>
                      selectWhere(
                        ev.Select('Id', 'Date', 'Start', 'End'),
                        e => f.event.kind == 'some' ? f.event.value(e) : e
                      )
                        .IncludeStrict('Location_ActivityEvent', q => q
                          .Select('LocationId', 'ActivityEventId')
                          .IncludeStrict('Location', q =>
                            selectWhere(
                              q.Select('Id', 'Title'),
                              q => f.location.kind == 'some' ? f.location.value(q) : q
                            )
                              .IncludeStrict('Region_Location', q => q
                                .Select('LocationId', 'RegionId')
                                .IncludeStrict('Region', q =>
                                  selectWhere(
                                    q.Select('Id', 'Title', 'Slug'),
                                    r => f.region.kind == 'some' ? f.region.value(r) : r
                                  )
                                )
                              )
                          )
                        )
                    )
                  )
              )
            )
        )
      ).ToList()
    //console.log("getActivityClusters", q)

    try {
      let result = q.then(async data => {
        let activityIds = data.reduce(
          (l, c) => {
            c.ActivityCluster_Activity.map(a => l = l.push(a.ActivityId))
            return l
          },
          List<number>()
        )

        let drupalActivitiesData = await getDrupalActivities(activityIds)
        let drupalActivities = drupalActivitiesData != 'loading' && drupalActivitiesData != 'error'
          ? List<DrupalModel.Activity>(drupalActivitiesData)
          : List<DrupalModel.Activity>()

        let props = data.reduce(
          (l, c) => {
            // Sort cluster activities by edition datetimes
            let clusterActivities = c.ActivityCluster_Activity
              .sort((a, b) => (
                a.Activity.first().Activity_ActivityEditionN.sort((c, d) => (
                  c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
                  - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
                )).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
                -
                b.Activity.first().Activity_ActivityEditionN.sort((c, d) => (
                  c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
                  - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
                )).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
              ))


            // Put StickyTip's in front, then Sticky's, then non sticky without loosing sorted order.
            clusterActivities = clusterActivities
              .filter(v => v.Activity.first().StickyTip)
              .concat(clusterActivities.filter(v => v.Activity.first().Sticky && !v.Activity.first().StickyTip))
              .concat(clusterActivities.filter(v => !v.Activity.first().Sticky && !v.Activity.first().StickyTip))

            let activitySummaries: List<ActivitySummaryProps> = clusterActivities
              .reduce(
                (activityAccumulator, cluster_activity) => {
                  // Skip activity if no Drupal counterpart was found
                  const metadata = drupalActivities.filter(dA => lastClusterOfDigits(dA.product_db_id) == cluster_activity.ActivityId.toString()).first()
                  if (!metadata) return activityAccumulator
                  
                  let dates = cluster_activity.Activity.first().Activity_ActivityEditionN.reduce(
                    (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date),
                    List<Date>()
                  ).sort((l, r) => l.getTime() - r.getTime())

                  let locations = cluster_activity.Activity.first().Activity_ActivityEditionN.reduce(
                    (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title),
                    List<string>()
                  ).toSet()

                  let regions = cluster_activity.Activity.first().Activity_ActivityEditionN.reduce(
                    (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Location_ActivityEvent.first().Location.first().Region_Location.first().Region.first().Title),
                    List<string>()
                  ).toSet()
                  if (regions.isEmpty()) return activityAccumulator

                  return activityAccumulator.push({
                    headingLevel: 'h3',
                    title: metadata.title,
                    date: dates.count() > 1 ? 'multiple' : dates.count() == 1 ? dates.first() : 'multiple',
                    location: locations.count() > 1 ? 'multiple' : locations.first(),
                    subtitle: metadata.description,
                    route: metadata.url,
                    imageAlt: metadata.image_alt,
                    imageUrl: fixMediaUrl(metadata.image_url),
                    isTip: cluster_activity.Activity.first()?.StickyTip || false
                  })
                },
                List<ActivitySummaryProps>()
              )

            if (activitySummaries.isEmpty()) return l

            return l.push({
              title: c.Title,
              slug: c.Slug,
              description: c.Description,
              activitySummaries
            })
          },
          List<ActivityClusterProps>()
        ).sort((a, b) => a.title.localeCompare(b.title))

        return props
      })

      return result as ApiResult<Promise<List<ActivityClusterProps>>>
    } catch (e) {
      console.log(e)
      return "error" as ApiResult<any>
    }
  }

export const getActivityEditions: (f: QueryConditions<ActivityClusterSelectedRelationQuery, ActivitySelectedQuery, EditionSelectedRelationQuery, RegionSelectedRelationQuery, LocationSelectedRelationQuery,
  EventSelectedRelationQuery>) => Promise<ApiResult<List<ActivityEditionN>>> =
  f => {
    let q = selectWhere(
      QueryContext.ActivityQuery(odataBaseUrl).Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'),
      q => f.activity.kind == 'some' ? f.activity.value(q) : q
    ).IncludeStrict('Activity_ActivityEditionN', q => q
      .Select('ActivityId', 'ActivityEditionNId')
      .IncludeStrict('ActivityEditionN', q =>
        selectWhere(
          q.Select('Id', 'SignupText', 'SignupURL', 'Cost'),
          e => f.edition.kind == 'some' ? f.edition.value(e) : e
        )
          .Include('ActivityEditionN_Book', aeb => aeb
            .Select('ActivityEditionNId', 'BookId')
            .Include('Book', b => b.Select('Id', 'Name', 'Author', 'Image', 'URL'))
          )
          .IncludeStrict('ActivityEditionN_ActivityEvent', ae => ae
            .Select('ActivityEditionNId', 'ActivityEventId')
            .IncludeStrict('ActivityEvent', ev => selectWhere(
              ev.Select('Id', 'Date', 'Start', 'End'),
              e => f.event.kind == 'some' ? f.event.value(e) : e
            )
              .IncludeStrict('Location_ActivityEvent', q => q
                .Select('LocationId', 'ActivityEventId')
                .IncludeStrict('Location', q =>
                  selectWhere(
                    q.Select('Id', 'Title'),
                    q => f.location.kind == 'some' ? f.location.value(q) : q
                  )
                    .IncludeStrict('Region_Location', q => q
                      .Select('LocationId', 'RegionId')
                      .IncludeStrict('Region', q =>
                        selectWhere(
                          q.Select('Id', 'Title', 'Slug'),
                          r => f.region.kind == 'some' ? f.region.value(r) : r
                        )
                      )
                    )
                )
              )
            )
          )
      )
    ).ToList()
    //console.log("getActivityEditions ", q)
    try {
      let result = q.then(
        async data => !data.isEmpty()
          ? data.first().Activity_ActivityEditionN.reduce(
            (l, a) => {
              let events: List<ActivityEvent> = List()

              a.ActivityEditionN.first().ActivityEditionN_ActivityEvent.forEach(x => {
                ;
                events = events.push({
                  id: x.ActivityEvent.first().Id,
                  date: x.ActivityEvent.first().Date,
                  start: x.ActivityEvent.first().Start,
                  end: x.ActivityEvent.first().End,
                  location: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title,
                  region: x.ActivityEvent.first().Location_ActivityEvent.first().Location.first().Region_Location.first().Region.first().Title
                })
              })

              if (events.isEmpty()) return l

              let activityBook = a.ActivityEditionN != null
                && a.ActivityEditionN.first() != null
                && a.ActivityEditionN.first().ActivityEditionN_Book != null
                && a.ActivityEditionN.first().ActivityEditionN_Book.first() != null
                && a.ActivityEditionN.first().ActivityEditionN_Book.first().Book != null ?
                a.ActivityEditionN.first().ActivityEditionN_Book.first().Book.first() : null

              return l.push({
                id: a.ActivityEditionN.first().Id,
                signupText: a.ActivityEditionN.first().SignupText,
                signupUrl: ensureValidUrl(a.ActivityEditionN.first().SignupURL),
                cost: a.ActivityEditionN.first().Cost,
                activityEvents: events,
                book: activityBook != null ? {
                  title: activityBook.Name,
                  author: activityBook.Author,
                  id: activityBook.Id,
                  url: activityBook.URL
                } : null,
              })
            },
            List<ActivityEditionN>()
          )
          : List()
      )
      //console.log("getActivityEditions ", result)
      return result as ApiResult<Promise<List<ActivityEditionN>>>
    } catch (e) {
      console.log(e)
      return "error" as ApiResult<any>
    }
  }

export const getActivities: (f: QueryConditions<ActivityClusterSelectedRelationQuery, ActivitySelectedQuery, EditionSelectedRelationQuery, RegionSelectedRelationQuery, LocationSelectedRelationQuery,
  EventSelectedRelationQuery>, datePostProcessor?: (dates: Iterable<number, Date>) => Date | "multiple") => Promise<ApiResult<List<ActivitySummaryProps>>> =
  (f, datePostProcessor = oneDateOrStringOfMultiple) => {
    let q = selectWhere(
      QueryContext.ActivityQuery(odataBaseUrl).Select('Title', 'Id', 'Sticky', 'HideInOverview', 'StickyTip'),
      q => f.activity.kind == 'some' ? f.activity.value(q) : q
    ).IncludeStrict('Activity_ActivityEditionN', q => q
      .Select('ActivityId', 'ActivityEditionNId')
      .IncludeStrict('ActivityEditionN', q =>
        selectWhere(
          q.Select('Id', 'SignupText', 'SignupURL', 'Cost'),
          e => f.edition.kind == 'some' ? f.edition.value(e) : e
        )
          .IncludeStrict('ActivityEditionN_ActivityEvent', ae => ae
            .Select('ActivityEditionNId', 'ActivityEventId')
            .IncludeStrict('ActivityEvent', ev =>
              selectWhere(
                ev.Select('Id', 'Date', 'Start', 'End'),
                e => f.event.kind == 'some' ? f.event.value(e) : e
              )
                .IncludeStrict('Location_ActivityEvent', q => q
                  .Select('LocationId', 'ActivityEventId')
                  .IncludeStrict('Location', q =>
                    selectWhere(
                      q.Select('Id', 'Title'),
                      q => f.location.kind == 'some' ? f.location.value(q) : q
                    )
                      .IncludeStrict('Region_Location', q => q
                        .Select('LocationId', 'RegionId')
                        .IncludeStrict('Region', q =>
                          selectWhere(
                            q.Select('Id', 'Title', 'Slug'),
                            r => f.region.kind == 'some' ? f.region.value(r) : r
                          )
                        )
                      )
                  )
                )
            )
          )
      )
    ).ToList()

    try {
      let result = q.then(async data => {
        let activityIds = data.reduce(
          (l, a) => l.push(a.Id),
          List<number>()
        )

        let drupalActivitiesData = await getDrupalActivities(activityIds)
        let drupalActivities = drupalActivitiesData != 'loading' && drupalActivitiesData != 'error'
          ? List<DrupalModel.Activity>(drupalActivitiesData)
          : List<DrupalModel.Activity>()

        // Sort activities by edition datetimes
        let activities = data
          .sort((a, b) => (
            a.Activity_ActivityEditionN.sort((c, d) => (
              c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
              - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
            )).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
            -
            b.Activity_ActivityEditionN.sort((c, d) => (
              c.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
              - d.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
            )).first().ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date.getTime()
          ))

        // Put StickyTip's in front, then Sticky's, then non sticky without loosing sorted order.
        activities = activities
          .filter(v => v.StickyTip)
          .concat(activities.filter(v => v.Sticky && !v.StickyTip))
          .concat(activities.filter(v => !v.Sticky && !v.StickyTip))

        return activities.reduce(
          (activityAccumulator, activity) => {
            let dates = activity.Activity_ActivityEditionN.reduce(
              (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Date),
              List<Date>()
            ).sort((l, r) => l.getTime() - r.getTime())

            let locations = activity.Activity_ActivityEditionN.reduce(
              (a, e) => a.push(e.ActivityEditionN.first().ActivityEditionN_ActivityEvent.first().ActivityEvent.first().Location_ActivityEvent.first().Location.first().Title),
              List<string>()
            ).toSet()

            const metadata = drupalActivities.filter(dA => lastClusterOfDigits(dA.product_db_id) == activity.Id.toString()).first()
            if (!metadata) return activityAccumulator

            return activityAccumulator.push({
              headingLevel: 'h4',
              title: metadata?.title,
              subtitle: metadata.description,
              location: locations.count() > 1 ? 'multiple' : locations.first(),
              date: datePostProcessor(dates),
              imageAlt: metadata.image_alt,
              imageUrl: fixMediaUrl(metadata.image_url),
              route: metadata.url,
              isTip: activity.StickyTip
            })
          },
          List<ActivitySummaryProps>()
        )
      })

      return result as ApiResult<Promise<List<ActivitySummaryProps>>>
    } catch (e) {
      console.log(e)
      return "error" as ApiResult<any>
    }
  }


const filterBy = (filter: FilterBuilderComplex<QueryContext.Location_ActivityEvent>, ids: List<number>): ComplexFilterExpresion => {
  const result = filter.ActivityEventId.Equals(ids.first())
  return ids.size == 1
    ? result
    : result.Or(filterBy(filter, ids.rest().toList()))
}

export interface Category {
  id: number;
  name: string;
}

export interface RegionFilterItem {
  id: number;
  name: string;
  slug: string;
}

export interface EventFilterResponse {
  categories: Category[];
  regions: RegionFilterItem[];
}

export interface EventFilter {
  categories: OrderedMap<number, string>
  regions: OrderedMap<number, RegionInfo>
}

export const getEventFilter = async (): Promise<EventFilter> => {
  const filterResult = await get<EventFilterResponse>('/api/v1/Event/GetEventFilters')
  if (filterResult == 'error' || filterResult == 'loading') return

  return {
    categories: OrderedMap<number, string>(filterResult.categories.map(c => [c.id, c.name])),
    regions: OrderedMap<number, RegionInfo>(filterResult.regions.map(c => [c.id, { name: c.name, slug: c.slug }]))
  }
}

export interface Book {
  Id: number
  Name: string
  Author: string
  Image: string
  URL: string
}

export interface Event {
  eventId: number
  activityId: number
  activityTitle: string
  clusterId: number
  clusterName: string
  date: Date
  start: string
  end: string
  cost: string
  book: Book
  locationId: number
  locationName: string
  signUpText: string
  signUpUrl: string
  sticky: boolean
  tip: boolean
  hide: boolean
  drupal_activity: DrupalModel.Activity
}

export interface EventResponse {
  count: number
  events: Event[]
  categoryEventCount: Map<string, number>
  regionEventCount: Map<string, number>
  anyRegionEventCount: number
  datesEventCount: DateRangeEventCount
}

interface ApiEvent extends Omit<Event, 'date'> { date: string }
interface ApiEventResponse extends Omit<EventResponse, 'events' | 'activities'> { events: ApiEvent[] }

export const getEvents = async (options: {
  selectedRegion: number
  selectedCategories: Set<number>
  page: number
  pageSize: number
  from: Date
  until: Date
  search: string
}): Promise<EventResponse> => {
  const { selectedRegion, selectedCategories, page, pageSize, from, until, search } = options
  const params: string[] = [
    `page=${page}`,
    `pageSize=${pageSize}`
  ]

  if (selectedRegion) {
    params.push(`regions=${selectedRegion}`)
  }

  if (from) {
    params.push(`from=${Moment(from).format("YYYY-MM-DD")}`)
  }

  if (until) {
    params.push(`until=${Moment(until).format("YYYY-MM-DD")}`)
  }

  if (search.trim()) {
    params.push(`query=${encodeURIComponent(search.trim())}`)
  }

  if (!selectedCategories.isEmpty()) {
    selectedCategories.forEach(categorie => {
      params.push(`categories=${categorie}`)
    });
  }

  const query = params.length == 0 ? '' : '?' + params.join('&')

  const eventResult = await get<ApiEventResponse>('/api/v1/Event/GetEvents' + query)
  if (eventResult == 'error' || eventResult == 'loading') return

  const convertedResult: EventResponse = {
    ...eventResult,
    events: eventResult.events.map(e => ({ ...e, date: new Date(e.date) })),
    categoryEventCount: Map<string, number>(eventResult.categoryEventCount),
    regionEventCount: Map<string, number>(eventResult.regionEventCount),
    anyRegionEventCount: eventResult.anyRegionEventCount,
    datesEventCount: eventResult.datesEventCount
  }

  return convertedResult;
}

const oneDateOrStringOfMultiple = (dates: Iterable<number, Date>) => dates.count() > 1 ? 'multiple' : dates.count() == 1 ? dates.first() : 'multiple'

export const getNavigation: (
  id: string
) => Promise<ApiResult<DrupalModel.NavigationItem[]>> = id =>
    get(`${drupalUrl}/api/menu_items/${id}`);

export const getDrupalActivities: (
  pdbIds: List<number>
) => Promise<ApiResult<DrupalModel.Activity[]>> = pdbIds =>
    get(`${drupalUrl}/api/activities/${pdbIds.join("+")}`);

export const getDrupalVacancies: (
  categoryId: List<number>
) => Promise<ApiResult<OverviewVacancy[]>> = categoryIds =>
    get(`${drupalUrl}/api/vacancies/${categoryIds.join("+")}`);

export const getDrupalLocations: (
  pdbIds: List<number>
) => Promise<ApiResult<DrupalModel.Location[]>> = pdbIds =>
    get(`${drupalUrl}/api/locations/${pdbIds.join("+")}`);

export const getAllDrupalLocations: () 
  => Promise<ApiResult<DrupalModel.Location[]>> = () =>
      get(`${drupalUrl}/api/locations/all`);

export const getDrupalReports: () => Promise<ApiResult<DrupalModel.Report[]>> = () =>
  get<DrupalModel.Report[]>(`${drupalUrl}/api/reports`)

export const getReports: () => Promise<ApiResult<Map<string, Report>>> = () =>
  new Promise<ApiResult<Map<string, Report>>>(async res => {
    let drupalReports = await getDrupalReports()
    let reports = Map<string, Report>()

    if (drupalReports != 'error' && drupalReports != 'loading') {
      drupalReports.forEach(r => {
        if (!reports.has(r.id)) {
          reports = reports.set(r.id, ({
            created: r.created,
            datetimes: Set(),
            imageAlt: r.imageAlt,
            imageUrl: r.imageUrl,
            locations: Set(),
            shortDescription: r.shortDescription,
            title: r.title,
            url: r.url
          }))
        }

        reports = reports.set(r.id, ({
          ...reports.get(r.id),
          datetimes: r.datetime != "" ? reports.get(r.id).datetimes.add(moment.unix(parseInt(r.datetime))) : reports.get(r.id).datetimes,
          locations: r.location != "" ? reports.get(r.id).locations.add(r.location) : reports.get(r.id).locations
        }))
      })

      reports = reports.sort((a, b) => {
        const dateA = a.datetimes.first()
        const dateB = b.datetimes.first()
        if (dateA == undefined || dateB == undefined) return 0
        return dateB.diff(dateA)
      }).toOrderedMap()

      res(reports)
    } else {
      res('error')
    }
  })



export const getTeasers: Fun<string[], Promise<ApiResult<Map<string, DrupalModel.Teaser>>>> = (ids) => new Promise(async res => {
  try {
    let teasers = await get<DrupalModel.Teaser[]>(`${drupalUrl}/api/teasers/${ids.join("+")}`)

    if (teasers == 'error' || teasers == 'loading') throw new Error('Could not load teasers')

    res(
      OrderedMap<string, DrupalModel.Teaser>(teasers.map(t => [t.id, t]))
        .sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id))
        .toOrderedMap()
    )
  } catch (e) {
    console.error(e)
    res('error')
  }
})

const isParsableUrl: (url: string) => boolean = (url) => {
  try {
    new URL(url)
    return true
  } catch (_) {
    return false
  }
}

const ensureValidUrl: (url: string) => string = (url) => {
  if (isParsableUrl(url)) return url

  const prefixed = 'https://' + url
  if (isParsableUrl(prefixed)) return prefixed

  return ""
}
